import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { collection, query, orderBy, getDocs, addDoc, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import emailToName from '../utils/emailToName';

const STATUS_OPTIONS = ['준비중', '진행중', '완료'];

// 일정 등록 모달
const ScheduleRegisterModal = ({ open, onClose, onRegister }: { open: boolean, onClose: () => void, onRegister: (title: string, description: string, dueDate: string) => void }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim() || !dueDate.trim()) return;
    setLoading(true);
    await onRegister(title, description, dueDate);
    setLoading(false);
    setTitle('');
    setDescription('');
    setDueDate('');
    onClose();
  };

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 10000 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 500, maxWidth: 700, minHeight: 400, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '20%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 18, color: '#1976d2' }}>일정 등록</div>
        <div style={{ marginBottom: 12 }}>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="제목" style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 4, border: '1px solid #bbb', marginBottom: 8 }} />
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="설명" style={{ width: '100%', fontSize: 15, padding: 8, borderRadius: 4, border: '1px solid #bbb', minHeight: 80, marginBottom: 8 }} />
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, marginRight: 8 }}>마감일 or 시행일:</span>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
        </div>
        <button style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 22px', fontWeight: 600, fontSize: 16, marginTop: 8, cursor: 'pointer' }} onClick={handleSubmit} disabled={loading}>{loading ? '등록중...' : '등록'}</button>
      </div>
    </div>
  );
};

// 일정 수정/등록 모달 (상태, 체크리스트 포함)
const ScheduleEditModal = ({ open, onClose, onSave, defaultValue, isEdit }: {
  open: boolean,
  onClose: () => void,
  onSave: (data: any) => void,
  defaultValue?: any,
  isEdit?: boolean
}) => {
  const [title, setTitle] = useState(defaultValue?.title || '');
  const [description, setDescription] = useState(defaultValue?.description || '');
  const [status, setStatus] = useState(defaultValue?.status || '준비중');
  const [dueDate, setDueDate] = useState(defaultValue?.dueDate || '');
  const [checklist, setChecklist] = useState(defaultValue?.checklist || [{ text: '', checked: false }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTitle(defaultValue?.title || '');
    setDescription(defaultValue?.description || '');
    setStatus(defaultValue?.status || '준비중');
    setDueDate(defaultValue?.dueDate || '');
    setChecklist(defaultValue?.checklist || [{ text: '', checked: false }]);
  }, [defaultValue, open]);

  const handleChecklistChange = (idx: number, value: string) => {
    setChecklist(cl => cl.map((item, i) => i === idx ? { ...item, text: value } : item));
  };
  const handleChecklistCheck = (idx: number, checked: boolean) => {
    setChecklist(cl => cl.map((item, i) => i === idx ? { ...item, checked } : item));
  };
  const handleAddChecklist = () => {
    setChecklist(cl => [...cl, { text: '', checked: false }]);
  };
  const handleRemoveChecklist = (idx: number) => {
    setChecklist(cl => cl.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!title.trim() || !dueDate.trim()) return;
    setLoading(true);
    await onSave({ title, description, status, dueDate, checklist });
    setLoading(false);
    onClose();
  };
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 10000 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 500, maxWidth: 700, minHeight: 400, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '20%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 18, color: '#1976d2' }}>{isEdit ? '일정 수정' : '일정 등록'}</div>
        <div style={{ marginBottom: 12 }}>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="제목" style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 4, border: '1px solid #bbb', marginBottom: 8 }} />
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="설명" style={{ width: '100%', fontSize: 15, padding: 8, borderRadius: 4, border: '1px solid #bbb', minHeight: 80, marginBottom: 8 }} />
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, marginRight: 8 }}>상태:</span>
            {STATUS_OPTIONS.map(opt => (
              <label key={opt} style={{ marginRight: 12 }}>
                <input type="radio" checked={status === opt} onChange={() => setStatus(opt)} /> {opt}
              </label>
            ))}
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600, marginRight: 8 }}>마감일 or 시행일:</span>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>준비항목:</span>
            {checklist.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <input type="checkbox" checked={item.checked} onChange={e => handleChecklistCheck(idx, e.target.checked)} style={{ marginRight: 6 }} />
                <input type="text" value={item.text} onChange={e => handleChecklistChange(idx, e.target.value)} placeholder="항목 입력" style={{ flex: 1, fontSize: 15, padding: 4, borderRadius: 4, border: '1px solid #bbb', marginRight: 4 }} />
                {checklist.length > 1 && <button onClick={() => handleRemoveChecklist(idx)} style={{ color: '#d32f2f', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>×</button>}
              </div>
            ))}
            <button onClick={handleAddChecklist} style={{ background: '#eee', color: '#1976d2', border: 'none', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer', marginTop: 4 }}>+ 항목 추가</button>
          </div>
        </div>
        <button style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 22px', fontWeight: 600, fontSize: 16, marginTop: 8, cursor: 'pointer' }} onClick={handleSubmit} disabled={loading}>{loading ? (isEdit ? '저장중...' : '등록중...') : (isEdit ? '저장' : '등록')}</button>
      </div>
    </div>
  );
};

// 일정 상세 모달 (상태, 체크리스트 포함)
const ScheduleDetailModal = ({ open, onClose, schedule, onEdit, onDelete, isMine }: { open: boolean, onClose: () => void, schedule: any, onEdit: () => void, onDelete: () => void, isMine: boolean }) => {
  if (!open || !schedule) return null;
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 10002 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 500, maxWidth: 700, minHeight: 300, maxHeight: '80vh', boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '20%', transform: 'translate(-50%, 0)', display: 'flex', flexDirection: 'column' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 22, color: '#1976d2', marginBottom: 12 }}>{schedule.title}</div>
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontWeight: 600, color: '#555' }}>[상태]</span> {schedule.status} &nbsp;&nbsp;
          <span style={{ fontWeight: 600, color: '#555' }}>[마감일 or 시행일]</span> {schedule.dueDate}
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#555' }}>[설명]</div>
          <div style={{ whiteSpace: 'pre-line', color: '#333', marginBottom: 8 }}>{schedule.description}</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#555' }}>[준비사항]</div>
          <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
            {schedule.checklist?.map((item: any, idx: number) => (
              <li key={idx} style={{ fontSize: 15, color: item.checked ? '#1976d2' : '#888', marginBottom: 2 }}>
                <input type="checkbox" checked={item.checked} readOnly style={{ marginRight: 6 }} />
                {item.text}
              </li>
            ))}
          </ul>
        </div>
        <div style={{ color: '#bbb', fontSize: 14, marginBottom: 18 }}>
          마감일 or 시행일: {schedule.dueDate} / 작성자: {emailToName[schedule.createdBy] || schedule.createdBy}
        </div>
        {isMine && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button 
              onClick={onEdit}
              style={{ background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 6, padding: '6px 12px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
            >수정</button>
            <button 
              onClick={onDelete}
              style={{ background: '#fff', color: '#d32f2f', border: '1px solid #d32f2f', borderRadius: 6, padding: '6px 12px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
            >삭제</button>
          </div>
        )}
      </div>
    </div>
  );
};

const CompanyScheduleBoardPage = () => {
  const [schedules, setSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editSchedule, setEditSchedule] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailSchedule, setDetailSchedule] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const PAGE_SIZE = 10;
  const user = auth.currentUser;

  // 일정 목록 불러오기
  const fetchSchedules = async () => {
    setLoading(true);
    const q = query(collection(db, 'companySchedules'), orderBy('dueDate', 'desc'));
    const snap = await getDocs(q);
    setSchedules(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    setLoading(false);
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  // 페이지네이션 계산
  const totalPages = Math.ceil(schedules.length / PAGE_SIZE);
  const pagedSchedules = schedules.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // 일정 등록
  const handleRegister = async (data: any) => {
    await addDoc(collection(db, 'companySchedules'), {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: user?.email || '',
    });
    fetchSchedules();
  };

  // 일정 수정
  const handleEdit = (schedule: any) => {
    setEditSchedule(schedule);
    setShowEdit(true);
    setShowDetail(false);
  };
  const handleEditSave = async (data: any) => {
    if (!editSchedule) return;
    await updateDoc(doc(db, 'companySchedules', editSchedule.id), {
      ...data,
      updatedAt: serverTimestamp(),
    });
    setShowEdit(false);
    setEditSchedule(null);
    fetchSchedules();
  };

  // 일정 삭제
  const handleDelete = async (id: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await deleteDoc(doc(db, 'companySchedules', id));
    setShowDetail(false);
    fetchSchedules();
  };

  // 일정 상세 보기
  const handleShowDetail = (schedule: any) => {
    setDetailSchedule(schedule);
    setShowDetail(true);
  };

  return (
    <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '36px clamp(20px, 4vw, 56px)' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 40 }}>
        <div style={{ fontWeight: 700, fontSize: 20, color: '#444444' }}>회사 주요일정 게시판</div>
        <button 
          onClick={() => setShowRegister(true)}
          style={{ 
            background: '#1976d2', 
            color: '#fff', 
            border: 'none', 
            borderRadius: 8, 
            padding: '12px 24px', 
            fontWeight: 600, 
            fontSize: 16, 
            cursor: 'pointer' 
          }}
        >
          일정 등록
        </button>
      </div>

      {/* 일정 목록 */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.10)', padding: 32 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>로딩 중...</div>
        ) : schedules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>등록된 일정이 없습니다.</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {pagedSchedules.map(schedule => {
                const isMine = user && schedule.createdBy === user.email;
                const isHovered = hoveredId === schedule.id;
                return (
                  <div
                    key={schedule.id}
                    style={{
                      borderBottom: '1px solid #eee',
                      padding: '18px 0',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: isHovered ? '#f5f7fa' : 'transparent'
                    }}
                    onMouseEnter={() => setHoveredId(schedule.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <div
                      onClick={() => handleShowDetail(schedule)}
                      style={{
                        flex: 1,
                        fontWeight: 700,
                        fontSize: 18,
                        color: isHovered ? '#1565c0' : '#1976d2',
                        textDecoration: isHovered ? 'underline' : 'none'
                      }}
                    >
                      <span style={{
                        fontSize: 15,
                        marginRight: 6,
                        color: schedule.status === '준비중' ? '#888' : schedule.status === '진행중' ? '#1976d2' : '#43a047',
                        fontWeight: 700
                      }}>
                        [{schedule.status}]
                      </span>
                      {schedule.title}
                    </div>
                    <div style={{ color: '#bbb', fontSize: 14, marginLeft: 24, minWidth: 180, textAlign: 'right' }}>
                      {schedule.dueDate} / {emailToName[schedule.createdBy] || schedule.createdBy}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32, gap: 8 }}>
                {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(num => (
                  <button
                    key={num}
                    onClick={() => setPage(num)}
                    style={{
                      minWidth: 36,
                      height: 36,
                      borderRadius: 6,
                      border: num === page ? '2px solid #1976d2' : '1px solid #ccc',
                      background: num === page ? '#1976d2' : '#fff',
                      color: num === page ? '#fff' : '#222',
                      fontWeight: 600,
                      fontSize: 15,
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* 모달들 */}
      <ScheduleRegisterModal 
        open={showRegister} 
        onClose={() => setShowRegister(false)} 
        onRegister={handleRegister} 
      />
      <ScheduleEditModal 
        open={showRegister} 
        onClose={() => setShowRegister(false)} 
        onSave={handleRegister} 
        defaultValue={{ title: '', description: '', status: '준비중', dueDate: '', checklist: [{ text: '', checked: false }] }}
        isEdit={false}
      />
      <ScheduleEditModal 
        open={showEdit} 
        onClose={() => { setShowEdit(false); setEditSchedule(null); }} 
        onSave={handleEditSave} 
        defaultValue={editSchedule} 
        isEdit={true}
      />
      <ScheduleDetailModal 
        open={showDetail} 
        onClose={() => setShowDetail(false)} 
        schedule={detailSchedule} 
        onEdit={() => handleEdit(detailSchedule)} 
        onDelete={() => handleDelete(detailSchedule?.id)} 
        isMine={user && detailSchedule && detailSchedule.createdBy === user.email} 
      />
    </div>
  );
};

export default CompanyScheduleBoardPage; 